<template>
    <section ref="sectionRef" class="relative py-16 sm:py-20 lg:py-28 overflow-hidden">

        <!-- Ambient blobs -->
        <div class="absolute inset-0 pointer-events-none">
            <div class="absolute top-0 left-1/4 w-96 h-96 rounded-full opacity-10"
                 style="background: radial-gradient(circle, #a75ded 0%, transparent 70%); filter: blur(90px);"></div>
            <div class="absolute bottom-0 right-1/4 w-80 h-80 rounded-full opacity-10"
                 style="background: radial-gradient(circle, #e879f9 0%, transparent 70%); filter: blur(90px);"></div>
        </div>

        <!-- Top divider -->
        <div class="absolute top-0 left-0 right-0 h-px"
             style="background: linear-gradient(90deg, transparent 0%, rgba(124,58,237,0.7) 20%, rgba(232,121,249,0.9) 50%, rgba(6,182,212,0.6) 80%, transparent 100%);"></div>

        <div class="relative z-10 px-6 sm:px-8 lg:px-12 xl:px-16 2xl:px-24">

            <!-- Section label -->
            <div class="flex justify-center mb-8 scroll-reveal">
                <div class="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border text-xs font-bold tracking-[0.15em] uppercase"
                     style="border-color: rgba(167,93,237,0.4); background: rgba(167,93,237,0.08); color: #c084fc;">
                    <span class="w-1.5 h-1.5 rounded-full animate-pulse inline-block" style="background:#a75ded;box-shadow:0 0 6px rgba(167,93,237,0.8)"></span>
                    {{ t('aboutUsLabel') }}
                </div>
            </div>

            <!-- Headline -->
            <div class="text-center mb-4 scroll-reveal">
                <h2 class="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black leading-tight tracking-tight">
                    <span class="text-white block">{{ t('aboutUsTitleLine1') }}</span>
                    <span class="block mt-1"
                          style="background: linear-gradient(135deg, #7c3aed 0%, #a75ded 40%, #e879f9 80%, #c084fc 100%); -webkit-background-clip: text; background-clip: text; color: transparent;">
                        {{ t('aboutUsTitleLine2') }}
                    </span>
                </h2>
            </div>

            <p class="text-center text-white/60 text-lg sm:text-xl max-w-3xl mx-auto mb-12 leading-relaxed font-light scroll-reveal">
                {{ t('aboutUsSubtitle') }}
            </p>

            <!-- Mission + Vision -->
            <div class="grid lg:grid-cols-2 gap-6 lg:gap-10 xl:gap-14 mb-12">

                <!-- Mission card -->
                <div class="relative group rounded-3xl p-8 sm:p-10 border transition-all duration-500 hover:scale-[1.02] hover:-translate-y-1 overflow-hidden scroll-reveal"
                     style="background: linear-gradient(145deg, rgba(124,58,237,0.24) 0%, rgba(10,4,22,0.97) 65%); border-color: rgba(124,58,237,0.38);">
                    <div class="absolute -top-12 -left-12 w-48 h-48 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                         style="background: radial-gradient(circle, rgba(124,58,237,0.25) 0%, transparent 70%); filter: blur(30px);"></div>
                    <div class="absolute top-0 left-0 right-0 h-[3px] rounded-t-3xl"
                         style="background: linear-gradient(90deg, #7c3aed, #a75ded, rgba(167,93,237,0.3));"></div>
                    <div class="relative z-10">
                        <div class="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                             style="background: linear-gradient(135deg, #7c3aed 0%, #a75ded 100%); box-shadow: 0 8px 24px rgba(167,93,237,0.4);">
                            <svg class="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 2a10 10 0 100 20A10 10 0 0012 2zm0 2a8 8 0 110 16A8 8 0 0112 4zm0 3a5 5 0 100 10A5 5 0 0012 7zm0 2a3 3 0 110 6A3 3 0 0112 9zm0 1a2 2 0 100 4 2 2 0 000-4z"/>
                            </svg>
                        </div>
                        <h3 class="text-2xl sm:text-3xl font-black text-white mb-2">{{ t('aboutUsMissionTitle') }}</h3>
                        <div class="w-12 h-0.5 mb-4 rounded-full" style="background: linear-gradient(90deg, #a75ded, transparent);"></div>
                        <p class="text-white/60 text-base sm:text-lg leading-relaxed font-light">{{ t('aboutUsMissionText') }}</p>
                    </div>
                </div>

                <!-- Vision card -->
                <div class="relative group rounded-3xl p-8 sm:p-10 border transition-all duration-500 hover:scale-[1.02] hover:-translate-y-1 overflow-hidden scroll-reveal"
                     style="background: linear-gradient(145deg, rgba(217,70,239,0.22) 0%, rgba(10,4,22,0.97) 65%); border-color: rgba(217,70,239,0.36);">
                    <div class="absolute -top-12 -right-12 w-48 h-48 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                         style="background: radial-gradient(circle, rgba(217,70,239,0.25) 0%, transparent 70%); filter: blur(30px);"></div>
                    <div class="absolute top-0 left-0 right-0 h-[3px] rounded-t-3xl"
                         style="background: linear-gradient(90deg, #d946ef, #e879f9, rgba(232,121,249,0.3));"></div>
                    <div class="relative z-10">
                        <div class="w-14 h-14 rounded-2xl flex items-center justify-center mb-5"
                             style="background: linear-gradient(135deg, #a75ded 0%, #e879f9 100%); box-shadow: 0 8px 24px rgba(232,121,249,0.4);">
                            <svg class="w-7 h-7 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17a5 5 0 110-10 5 5 0 010 10zm0-8a3 3 0 100 6 3 3 0 000-6z"/>
                            </svg>
                        </div>
                        <h3 class="text-2xl sm:text-3xl font-black text-white mb-2">{{ t('aboutUsVisionTitle') }}</h3>
                        <div class="w-12 h-0.5 mb-4 rounded-full" style="background: linear-gradient(90deg, #e879f9, transparent);"></div>
                        <p class="text-white/60 text-base sm:text-lg leading-relaxed font-light">{{ t('aboutUsVisionText') }}</p>
                    </div>
                </div>
            </div>

            <!-- Core Values -->
            <div class="mb-14">
                <div class="text-center mb-10">
                    <h3 class="text-2xl sm:text-3xl lg:text-4xl font-black text-white mb-3 scroll-reveal">{{ t('aboutUsCoreValuesTitle') }}</h3>
                    <p class="text-white/50 text-base sm:text-lg max-w-xl mx-auto scroll-reveal">{{ t('aboutUsCoreValuesSubtitle') }}</p>
                </div>
                <div class="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
                    <div v-for="(val, idx) in coreValues" :key="idx"
                         class="group relative rounded-2xl p-6 border transition-all duration-400 hover:scale-[1.04] hover:-translate-y-1 cursor-default overflow-hidden scroll-reveal"
                         :style="{ background: coreValueStyles[idx].bg, borderColor: coreValueStyles[idx].border }">
                        <div class="absolute top-0 left-0 right-0 h-[3px] rounded-t-2xl"
                             :style="{ background: coreValueStyles[idx].accent }"></div>
                        <div class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                             :style="{ background: `radial-gradient(circle at 30% 40%, ${coreValueStyles[idx].glow}, transparent 65%)` }"></div>
                        <div class="w-11 h-11 rounded-xl flex items-center justify-center mb-4 transition-transform duration-300 group-hover:scale-110"
                             :style="valueIconStyle(idx)">
                            <svg class="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                                <path :d="val.icon"/>
                            </svg>
                        </div>
                        <h4 class="text-white font-bold text-base mb-2">{{ t(val.titleKey) }}</h4>
                        <p class="text-white/50 text-sm leading-relaxed">{{ t(val.descKey) }}</p>
                    </div>
                </div>
            </div>

            <!-- Story block -->
            <div class="relative rounded-3xl p-6 sm:p-10 lg:p-14 border overflow-hidden scroll-reveal"
                 style="background: rgba(10,4,22,0.92); border-color: rgba(167,93,237,0.2);">
                <div class="absolute top-4 left-6 sm:top-8 sm:left-10 text-[8rem] sm:text-[12rem] font-black leading-none select-none pointer-events-none"
                     style="color: rgba(167,93,237,0.06);">&ldquo;</div>
                <div class="absolute bottom-0 right-0 w-80 h-80 rounded-full pointer-events-none"
                     style="background: radial-gradient(circle, rgba(167,93,237,0.08) 0%, transparent 70%); filter: blur(60px);"></div>
                <div class="absolute top-0 left-0 right-0 h-px"
                     style="background: linear-gradient(90deg, transparent, rgba(167,93,237,0.5), rgba(232,121,249,0.4), transparent);"></div>

                <div class="relative z-10 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center">
                    <div class="lg:col-span-7 space-y-6">
                        <div class="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-widest"
                             style="background: rgba(167,93,237,0.12); border: 1px solid rgba(167,93,237,0.3); color: #c084fc;">
                            {{ t('aboutUsOurStoryLabel') }}
                        </div>
                        <h3 class="text-3xl sm:text-4xl font-black text-white leading-tight">{{ t('aboutUsStoryTitle') }}</h3>
                        <p class="text-white/55 text-base sm:text-lg leading-relaxed font-light">{{ t('aboutUsStoryPara1') }}</p>
                        <p class="text-white/55 text-base sm:text-lg leading-relaxed font-light">{{ t('aboutUsStoryPara2') }}</p>
                    </div>

                    <div class="lg:col-span-5 space-y-4">
                        <div v-for="(pillar, idx) in pillars" :key="idx"
                             class="flex items-start gap-4 p-4 sm:p-5 rounded-xl border transition-all duration-300 hover:scale-[1.02] hover:border-purple-500/30"
                             style="background: rgba(255,255,255,0.025); border-color: rgba(255,255,255,0.07);">
                            <div class="w-9 h-9 flex-shrink-0 rounded-lg flex items-center justify-center mt-0.5"
                                 :style="pillarIconStyle(idx)">
                                <svg class="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                                    <path :d="pillar.icon"/>
                                </svg>
                            </div>
                            <div>
                                <h5 class="text-white font-bold text-sm mb-1">{{ t(pillar.titleKey) }}</h5>
                                <p class="text-white/45 text-sm leading-relaxed">{{ t(pillar.descKey) }}</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Bottom divider -->
        <div class="absolute bottom-0 left-0 right-0 h-px"
             style="background: linear-gradient(90deg, transparent 0%, rgba(167,93,237,0.3) 30%, rgba(232,121,249,0.4) 50%, rgba(167,93,237,0.3) 70%, transparent 100%);"></div>
    </section>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useTranslation } from '@/composables/useTranslation'

const { t } = useTranslation()
const sectionRef = ref(null)

const valueIconStyle = (idx) => [0, 2].includes(idx)
    ? { background: 'linear-gradient(135deg,#7c3aed,#a75ded)', boxShadow: '0 4px 16px rgba(167,93,237,0.3)' }
    : { background: 'linear-gradient(135deg,#a75ded,#e879f9)', boxShadow: '0 4px 16px rgba(232,121,249,0.3)' }

const pillarIconStyle = (idx) => idx % 2 === 0
    ? { background: 'linear-gradient(135deg,#7c3aed,#a75ded)', boxShadow: '0 4px 12px rgba(167,93,237,0.25)' }
    : { background: 'linear-gradient(135deg,#a75ded,#e879f9)', boxShadow: '0 4px 12px rgba(232,121,249,0.25)' }

const coreValueStyles = [
    { bg: 'linear-gradient(145deg, rgba(124,58,237,0.20) 0%, rgba(10,4,22,0.97) 70%)',  border: 'rgba(124,58,237,0.35)', accent: 'linear-gradient(90deg,#7c3aed,#a75ded)', glow: 'rgba(124,58,237,0.15)' },
    { bg: 'linear-gradient(145deg, rgba(6,182,212,0.18) 0%, rgba(10,4,22,0.97) 70%)',   border: 'rgba(6,182,212,0.32)',  accent: 'linear-gradient(90deg,#06b6d4,#67e8f9)', glow: 'rgba(6,182,212,0.13)' },
    { bg: 'linear-gradient(145deg, rgba(217,70,239,0.20) 0%, rgba(10,4,22,0.97) 70%)',  border: 'rgba(217,70,239,0.34)', accent: 'linear-gradient(90deg,#d946ef,#e879f9)', glow: 'rgba(217,70,239,0.14)' },
    { bg: 'linear-gradient(145deg, rgba(99,102,241,0.20) 0%, rgba(10,4,22,0.97) 70%)',  border: 'rgba(99,102,241,0.35)', accent: 'linear-gradient(90deg,#4f46e5,#818cf8)', glow: 'rgba(99,102,241,0.14)' },
]

const coreValues = [
    { titleKey:'aboutUsValueTransparencyTitle', descKey:'aboutUsValueTransparencyDesc', icon:'M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z' },
    { titleKey:'aboutUsValueSecurityTitle',     descKey:'aboutUsValueSecurityDesc',     icon:'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z' },
    { titleKey:'aboutUsValueInnovationTitle',   descKey:'aboutUsValueInnovationDesc',   icon:'M13 10V3L4 14h7v7l9-11h-7z' },
    { titleKey:'aboutUsValueInclusionTitle',    descKey:'aboutUsValueInclusionDesc',    icon:'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20a3 3 0 01-3-3v-2a3 3 0 013-3h3a3 3 0 013 3v2a3 3 0 01-3 3m0 0H7' },
]

const pillars = [
    { titleKey:'aboutUsPillarBlockchainTitle', descKey:'aboutUsPillarBlockchainDesc', icon:'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4' },
    { titleKey:'aboutUsPillarDeFiTitle',       descKey:'aboutUsPillarDeFiDesc',       icon:'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z' },
    { titleKey:'aboutUsPillarAuditTitle',      descKey:'aboutUsPillarAuditDesc',      icon:'M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4' },
]

onMounted(() => {
    if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(entries => {
            entries.forEach(e => { if (e.isIntersecting) { e.target.classList.add('in-view') } })
        }, { threshold: 0.12 })
        document.querySelectorAll('.scroll-reveal').forEach(el => io.observe(el))
    }
})
</script>

<style scoped>
.scroll-reveal { opacity: 0; transform: translateY(28px); transition: opacity 0.7s ease, transform 0.7s ease; }
.scroll-reveal.in-view { opacity: 1; transform: none; }
@media (prefers-reduced-motion: reduce) {
    .scroll-reveal { opacity: 1; transform: none; transition: none; }
}
</style>
